import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { 
  generateLocalBusinessSchema, 
  generateOrganizationSchema,
  combineSchemas 
} from '../utils/schemaGenerator';
import {
  Phone,
  CheckCircle2,
  MapPin,
  Home as HomeIcon,
  Sparkles,
  Calendar,
  Key,
  Star,
  ArrowRight
} from 'lucide-react';

const HomePage = () => {
  const services = [
    {
      icon: Key,
      title: "Move-In / Move-Out Cleaning",
      description: "Complete deep cleaning for seamless transitions"
    },
    {
      icon: Sparkles,
      title: "Deep Cleaning",
      description: "Thorough attention to every corner of your home"
    },
    {
      icon: Calendar,
      title: "Recurring Cleaning",
      description: "Consistent service on your schedule"
    },
    {
      icon: HomeIcon,
      title: "Airbnb / Short-Term Rental Turnovers",
      description: "Fast, reliable turnovers for vacation properties"
    }
  ];

  // Structured Data for Homepage - using dynamic URL generator
  const structuredData = combineSchemas(
    generateLocalBusinessSchema(),
    generateOrganizationSchema()
  );

  return (
    <>
      <SEO 
        title="Pierce Premier Cleaning Services | Brevard County, FL"
        description="Professional house cleaning, deep cleaning, move-in/move-out, Airbnb and commercial cleaning in Palm Bay, Melbourne and throughout Brevard County, FL. Local, insured and 5-star rated."
        canonical="/"
        structuredData={structuredData}
      />
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-20 pb-0 overflow-hidden">
        {/* Background Image with Refined Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_pierce-premier/artifacts/p6rdh9x3_Screenshot%202026-03-13%20131233.png"
            alt="Bob and Heather Pierce, Owners"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 30%' }}
          />
          {/* Reduced opacity overlay for more visibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 via-blue-800/60 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-36 lg:py-44">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/95 backdrop-blur-sm px-6 py-3 rounded-full mb-10 shadow-lg">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-gray-900">Proudly Serving Brevard County, Florida</span>
            </div>

            {/* Headline - Larger and more prominent */}
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight drop-shadow-lg">
              Locally Owned.<br />
              Personally Invested.
            </h1>

            {/* Subheadline - More spacing */}
            <p className="text-2xl md:text-3xl text-white mb-8 font-light drop-shadow-md">
              Serving Brevard County, Florida with reliable, high-quality cleaning you can count on — every time.
            </p>

            {/* Body - More spacing */}
            <p className="text-lg text-white mb-12 leading-relaxed max-w-2xl drop-shadow-md">
              Meet Heather and Bob, the owners behind Pierce Premier Cleaning Services. When you hire us, you're working directly with a local husband and wife team committed to delivering consistent, high-quality results with personal attention to detail.
            </p>

            {/* Buttons - Larger with more spacing and shadow */}
            <div className="flex flex-col sm:flex-row gap-5 mb-12">
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-12 py-8 shadow-2xl hover:shadow-xl transition-all">
                  Get Free Quote
                </Button>
              </Link>
              <a href="tel:3214081347">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm text-lg px-12 py-8 shadow-xl hover:shadow-2xl transition-all">
                  <Phone className="mr-2 w-5 h-5" />
                  Call 321-408-1347
                </Button>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-8 text-white">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-blue-300" />
                <span className="text-sm font-medium">Locally Owned</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-blue-300" />
                <span className="text-sm font-medium">Husband & Wife Operated</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-blue-300" />
                <span className="text-sm font-medium">5-Star Rated</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Local Matters Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Local Matters
            </h2>
          </div>

          {/* Narrower text width with more spacing */}
          <div className="max-w-3xl mx-auto text-gray-700 leading-relaxed space-y-8 text-lg">
            <p>
              Many cleaning companies operate across multiple markets, which can sometimes lead to inconsistent service and a lack of familiarity with local needs.
            </p>

            {/* Emphasized line - larger and bold */}
            <p className="text-3xl font-bold text-gray-900 my-10">
              At Pierce Premier Cleaning Services, we take a different approach.
            </p>

            {/* Highlighted line */}
            <p className="text-xl font-semibold text-blue-700">
              We live here. We work here. And we stand behind every job we take on.
            </p>

            <p>
              Heather brings over 15 years of executive housekeeping experience, and together we've built a company focused on consistency, trust, and attention to detail.
            </p>

            <p>
              With Pierce Premier Cleaning, you'll always know who you're working with — and what to expect.
            </p>
          </div>

          {/* Local Badge - More refined and professional */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center space-x-3 bg-white border-2 border-blue-600 px-10 py-5 rounded-full shadow-xl">
              <MapPin className="w-6 h-6 text-blue-600" />
              <span className="text-xl font-bold text-gray-900">Locally owned and operated in Brevard County</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Professional Cleaning Services
            </h2>
            <p className="text-xl text-gray-600">
              Customized to your home, your needs, and your schedule.
            </p>
          </div>

          {/* Increased spacing between services */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {services.map((service, index) => (
              <div key={index} className="text-center group">
                {/* Smaller, cleaner icons */}
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <service.icon className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 leading-snug">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link to="/services">
              <Button variant="outline" size="lg" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust / Reviews Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              5-Star Rated. Locally Trusted.
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              We've built our reputation through consistent results and strong client relationships across Brevard County. We treat every home like it's our own — because our name is on every job.
            </p>
          </div>

          {/* Thumbtack Widget */}
          <div className="flex justify-center mb-16">
            <div className="widget bg-white border-2 border-gray-200 rounded-xl p-8 shadow-xl" id="tt-review-widget-star">
              <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/logos/thumbtack/wordmark.svg" alt="Thumbtack" className="tt-logo h-6 mb-4 mx-auto" />
              <a target="_blank" href="https://www.thumbtack.com/fl/palm-bay/house-cleaning/pierce-premier-cleaning-services/service/574132948530225153" className="text-gray-900 font-semibold hover:text-blue-600 transition-colors text-lg">
                <div>Pierce Premier Cleaning Services</div>
              </a>
              {/* Larger star ratings */}
              <div id="tt-dynamic" className="flex items-center justify-center gap-2 mt-4">
                <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg" className="w-7 h-7" alt="star" />
                <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg" className="w-7 h-7" alt="star" />
                <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg" className="w-7 h-7" alt="star" />
                <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg" className="w-7 h-7" alt="star" />
                <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg" className="w-7 h-7" alt="star" />
                <span className="text-gray-700 font-semibold ml-2 text-lg">7 reviews</span>
              </div>
            </div>
          </div>

          {/* Reviews Grid - More spacing and shadow */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
            <Card className="border-0 shadow-2xl hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                {/* Larger stars */}
                <div className="flex items-center justify-center mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-7 h-7 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-700 leading-relaxed mb-6 italic">
                  "Heather and Bob did an outstanding job cleaning our home. They were extremely thorough, professional, and paid attention to every detail. The house looked amazing when they finished and communication was excellent. Highly recommend their cleaning service."
                </blockquote>
                <div className="border-t border-gray-200 pt-4 text-center">
                  <div className="inline-flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-gray-900 text-sm">Verified Thumbtack Client</span>
                  </div>
                  <p className="text-gray-600 text-sm mt-2">Brevard County, FL</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-2xl hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-center justify-center mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-7 h-7 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-700 leading-relaxed mb-6 italic">
                  "Pierce Premier Cleaning Services responded to my last minute need of cleaning promptly and got our new house nice, clean and ready to move into! The house smelled so good and it felt more like home after they left. They are definitely very nice, professional, and I can tell they value their work! Thank you so so much!"
                </blockquote>
                <div className="border-t border-gray-200 pt-4 text-center">
                  <div className="inline-flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-gray-900 text-sm">Cassandra</span>
                  </div>
                  <p className="text-gray-600 text-sm mt-2">Verified Thumbtack Client</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-2xl hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-center justify-center mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-7 h-7 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-700 leading-relaxed mb-6 italic">
                  "The Pierces were very friendly and easy to work with. Their work was thorough and I am very glad to have had their help! They made moving in a breeze! I would recommend them to anyone with high standards of cleanliness."
                </blockquote>
                <div className="border-t border-gray-200 pt-4 text-center">
                  <div className="inline-flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-gray-900 text-sm">Jennifer J.</span>
                  </div>
                  <p className="text-gray-600 text-sm mt-2">Verified Thumbtack Client</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
            Let's Get Your Home Taken Care Of
          </h2>
          {/* Narrower text width */}
          <p className="text-xl text-gray-700 mb-10 leading-relaxed max-w-2xl mx-auto">
            We offer quick walkthroughs and personalized quotes so you know exactly what to expect. No pressure. Just reliable, professional service.
          </p>

          {/* More subtle tagline */}
          <p className="text-xs text-gray-500 mb-10 uppercase tracking-wider font-medium">
            Quick quotes. No pressure.
          </p>

          {/* More spacing and dominant primary button */}
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-12 py-8 shadow-xl hover:shadow-2xl transition-all">
                Request a Quote
              </Button>
            </Link>
            <a href="tel:3214081347">
              <Button size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-lg px-10 py-8 shadow-lg hover:shadow-xl transition-all">
                <Phone className="mr-2 w-5 h-5" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default HomePage;
